import type React from "react";
import { useEffect, useState } from "react";
import { Mail, Lock, Code2 } from "lucide-react";
import DecorativeNumbers from "../components/login/DecorativeNumbers";
import CustomInput from "../components/CustomInput";
import { useLogin } from "../hooks/useLogin";

const LoginForm: React.FC = () => {
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({ name: "", password: "", general: "" });

  useEffect(() => {
    localStorage.clear();
  }, []);

  const { mutate: login, isPending } = useLogin({
    onError: (errorMessage) => {
      setErrors((prev) => ({
        ...prev,
        general: errorMessage,
      }));
    },
  });

  const validateName = (name: string) => {
    return name.trim().length > 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = { name: "", password: "", general: "" };

    if (!name) {
      newErrors.name = "Name or email is required";
    } else if (!validateName(name)) {
      newErrors.name = "Please enter a valid name or email";
    }

    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    setErrors(newErrors);

    if (!newErrors.name && !newErrors.password) {
      login({ name, password });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 relative overflow-hidden flex items-center justify-center p-4">
      <DecorativeNumbers />
      <div className="w-full max-w-md shadow-2xl rounded-xl bg-white/80 backdrop-blur-sm border border-gray-100">
        <div className="text-center space-y-4 p-8 pb-6">
          <div className="mx-auto w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mb-2">
            <Code2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Welcome Back</h1>
          <p className="text-gray-600">Sign in to your DemoShortCode account</p>
        </div>
        <div className="px-8 pb-8 space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <CustomInput
              type="text"
              placeholder="Enter your name or email"
              value={name}
              onChange={setName}
              error={errors.name}
              icon={
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              }
            />
            <CustomInput
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={setPassword}
              error={errors.password}
              icon={
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              }
              showPassword={showPassword}
              toggleShowPassword={() => setShowPassword(!showPassword)}
            />
            {errors.general && (
              <p className="text-red-500 text-sm text-center">
              invalid credentials
              </p>
            )}
            <button
              type="submit"
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
              disabled={isPending}
            >
              {isPending ? "Signing In..." : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
